import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { getCartCount } from "@/utils/cart";
import { sanitizeHtmlRaw } from "@/components/tabs/ContentTab";
import { AboutUsRenderer } from "@/components/templates/AboutUsTemplate";
import { ContactUsRenderer } from "@/components/templates/ContactUsTemplate";
import { TermsOfUseRenderer } from "@/components/templates/TermsOfUseTemplate";
import { ShippingPolicyRenderer } from "@/components/templates/ShippingPolicyTemplate";
import { ReturnRefundRenderer } from "@/components/templates/ReturnRefundTemplate";
import { CancellationPolicyRenderer } from "@/components/templates/CancellationPolicyTemplate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StorefrontPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const cartCount = getCartCount();

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    axios.get(`${API}/pages/${slug}`)
      .then(r => setPage(r.data))
      .catch(e => {
        if (e.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <div className="min-h-screen bg-white">
      <SiteHeader cartCount={cartCount} showNav={false} />
      <div className="max-w-[1800px] mx-auto px-4 py-16 flex items-center justify-center">
        <div className="space-y-4 w-full max-w-2xl animate-pulse">
          <div className="h-8 bg-slate-100 rounded w-1/2" />
          <div className="h-4 bg-slate-100 rounded w-full" />
          <div className="h-4 bg-slate-100 rounded w-5/6" />
          <div className="h-4 bg-slate-100 rounded w-4/6" />
        </div>
      </div>
      <SiteFooter />
    </div>
  );

  if (notFound) return (
    <div className="min-h-screen bg-white">
      <SiteHeader cartCount={cartCount} showNav={false} />
      <div className="max-w-[1800px] mx-auto px-4 py-24 text-center">
        <p className="text-5xl font-black text-slate-200 mb-4">404</p>
        <h1 className="text-2xl font-bold text-slate-800 mb-2">Page Not Found</h1>
        <p className="text-slate-500 mb-6">The page <code className="bg-slate-100 px-2 py-0.5 rounded text-sm">/{slug}</code> doesn't exist or isn't published.</p>
        <Link to="/" className="inline-block bg-red-600 hover:bg-red-700 text-white font-semibold px-6 py-2.5 rounded-lg transition-colors">
          Back to Home
        </Link>
      </div>
      <SiteFooter />
    </div>
  );

  const isFullWidth = page.template === "Full Width";
  const isLanding = page.template === "Landing";
  // A page whose content contains a <style> block or full HTML boilerplate was
  // written as a full HTML layout. Treat it as a landing page.
  const isRawHtmlPage = !!(
    page.content &&
    (page.content.includes("<style") || page.content.includes("<!DOCTYPE") || page.content.includes("<html"))
  );
  const treatAsLanding = isLanding || isRawHtmlPage;
  const isAboutUs = page.template === "About Us";
  const isContactUs = page.template === "Contact Us";
  const isTermsOfUse = page.template === "Terms of Use";
  const isShippingPolicy = page.template === "Shipping Policy";
  const isReturnRefund = page.template === "Return & Refund Policy";
  const isCancellation = page.template === "Cancellation Policy";
  const isStructuredTemplate = isAboutUs || isContactUs || isTermsOfUse || isShippingPolicy || isReturnRefund || isCancellation;
  const isSidebarLeft = page.template === "Sidebar Left";
  const isSidebarRight = page.template === "Sidebar Right";
  const hasSidebar = isSidebarLeft || isSidebarRight;

  const renderTemplate = () => {
    if (isAboutUs) return <AboutUsRenderer data={page.template_data} />;
    if (isContactUs) return <ContactUsRenderer data={page.template_data} />;
    if (isTermsOfUse) return <TermsOfUseRenderer data={page.template_data} />;
    if (isShippingPolicy) return <ShippingPolicyRenderer data={page.template_data} />;
    if (isReturnRefund) return <ReturnRefundRenderer data={page.template_data} />;
    if (isCancellation) return <CancellationPolicyRenderer data={page.template_data} />;
    return null;
  };

  // Landing / full-HTML pages: render in a full-screen iframe so the page's own
  // CSS and JS are completely isolated from the React app (no Tailwind conflicts).
  if (treatAsLanding) {
    if (page.seo_title) document.title = page.seo_title;
    return (
      <FullPageIframe slug={slug} title={page.seo_title || page.title} />
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {page.seo_title && (document.title = page.seo_title)}

      <SiteHeader cartCount={cartCount} showNav={true} />

      {/* Breadcrumb — hidden for structured templates */}
      {!isStructuredTemplate && (
        <div className="bg-slate-50 border-b border-slate-100 py-4 px-4">
          <div className="max-w-[1800px] mx-auto flex items-center gap-2 text-sm text-slate-500">
            <Link to="/" className="hover:text-red-600 transition-colors">Home</Link>
            <span>/</span>
            <span className="text-slate-800 font-medium">{page.title}</span>
          </div>
        </div>
      )}

      {/* Content area */}
      <main className={(isFullWidth || isStructuredTemplate) ? "" : "max-w-[1800px] mx-auto px-4 py-8"}>
        {isStructuredTemplate ? (
          renderTemplate()
        ) : hasSidebar ? (
          <div className={`flex gap-8 ${isSidebarLeft ? "flex-row" : "flex-row-reverse"}`}>
            <aside className="w-64 flex-shrink-0">
              <div className="bg-slate-50 rounded-xl p-5 sticky top-20">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">On This Page</p>
                <p className="text-xs text-slate-400">Navigation auto-generates from headings</p>
              </div>
            </aside>
            <div className="flex-1 min-w-0">
              {!page.hide_title && <PageTitle page={page} />}
              <PageContent content={page.content} />
            </div>
          </div>
        ) : (
          <div className={isFullWidth ? "w-full" : "max-w-3xl"}>
            {!page.hide_title && <PageTitle page={page} />}
            <PageHeroMedia page={page} />
            <PageContent content={page.content} fullWidth={isFullWidth} />
          </div>
        )}
      </main>

      <SiteFooter />
    </div>
  );
}

/**
 * Full-screen iframe that auto-grows to fit the page's content height.
 * Used for Landing / full-HTML pages so the page's own CSS and JS are
 * completely isolated from the React app — no Tailwind conflicts.
 */
function FullPageIframe({ slug, title }) {
  const iframeRef = useRef(null);
  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    const resize = () => {
      try {
        const h = iframe.contentDocument?.documentElement?.scrollHeight
          || iframe.contentDocument?.body?.scrollHeight;
        if (h && h > 100) iframe.style.height = `${h}px`;
      } catch (_) {}
    };
    iframe.addEventListener("load", resize);
    // Poll once after a short delay to catch lazy-rendered content
    const t = setTimeout(resize, 600);
    return () => { iframe.removeEventListener("load", resize); clearTimeout(t); };
  }, [slug]);

  return (
    <iframe
      ref={iframeRef}
      src={`${API_BASE}/api/pages/${slug}/raw`}
      title={title || slug}
      style={{
        width: "100%",
        minHeight: "100vh",
        border: "none",
        display: "block",
        overflow: "hidden",
      }}
      scrolling="no"
    />
  );
}

function isVideoUrl(url) {
  return url && /\.(mp4|webm|mov|ogv)(\?.*)?$/i.test(url);
}

function HeroMedia({ url, alt }) {
  if (!url) return null;
  if (isVideoUrl(url)) {
    return <video src={url} autoPlay loop muted playsInline className="w-full rounded-xl object-cover max-h-80" />;
  }
  return <img src={url} alt={alt || ""} className="w-full rounded-xl object-cover max-h-80" />;
}

function PageHeroMedia({ page }) {
  const desktop = page.hero_desktop;
  const mobile = page.hero_mobile || page.hero_desktop; // fallback
  if (!desktop && !mobile) return null;
  return (
    <div className="mb-6">
      {/* Desktop */}
      {desktop && (
        <div className="hidden md:block">
          <HeroMedia url={desktop} alt={page.title} />
        </div>
      )}
      {/* Mobile */}
      {mobile && (
        <div className="block md:hidden">
          <HeroMedia url={mobile} alt={page.title} />
        </div>
      )}
    </div>
  );
}

function PageTitle({ page }) {
  return (
    <div className="mb-8 pb-6 border-b border-slate-100">
      <h1 className="text-3xl font-black text-slate-900">{page.title}</h1>
    </div>
  );
}

function PageContent({ content, fullWidth }) {
  if (!content) return (
    <p className="text-slate-400 italic">This page has no content yet.</p>
  );

  // Sanitize (strip scripts + on* handlers) but do NOT scope <style> blocks —
  // pages are standalone layouts so their CSS must work globally, not just
  // within a container div.
  const sanitized = sanitizeHtmlRaw(content);

  // If the content carries a <style> block it's raw-HTML layout; skip prose so
  // Tailwind typography doesn't fight the custom CSS.
  const isRawHtml = sanitized.includes("<style");

  return isRawHtml ? (
    <div
      className="raw-html-content"
      dangerouslySetInnerHTML={{ __html: sanitized }}
    />
  ) : (
    <div
      className={`prose prose-slate max-w-none ${fullWidth ? "" : "prose-base"} 
        prose-headings:font-black prose-h1:text-3xl prose-h2:text-2xl prose-h3:text-xl
        prose-a:text-red-600 prose-a:no-underline hover:prose-a:underline
        prose-img:rounded-xl prose-img:shadow-md
        [&_iframe]:w-full [&_iframe]:rounded-xl [&_iframe]:aspect-video [&_iframe]:my-4
        [&_blockquote]:border-l-4 [&_blockquote]:border-red-500 [&_blockquote]:bg-red-50 [&_blockquote]:px-4 [&_blockquote]:py-2 [&_blockquote]:rounded-r-lg
      `}
      dangerouslySetInnerHTML={{ __html: sanitized }}
    />
  );
}
